import type { ReactNode } from 'react'
import { BellRing, Check, Clock, Monitor, Search, ShieldAlert, Volume2, Wifi } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Product } from '@/lib/products'
import { BrowserFrame, PhoneFrame } from './DeviceFrame'

/**
 * The visual "face" of each product: a real screenshot in a device frame
 * when we have one, or an illustrated interface preview when we don't
 * (Crypto, Masar AI). `variant="card"` is the compact version for grids.
 */
export default function ProductVisual({
  product,
  variant = 'hero',
  className,
}: {
  product: Product
  variant?: 'hero' | 'card'
  className?: string
}) {
  const compact = variant === 'card'

  const content = (() => {
    switch (product.id) {
      case 'bms':
        return <BmsVisual compact={compact} host={product.appHost} />
      case 'bell':
        return <BellVisual compact={compact} />
      case 'mosque':
        return <MosqueVisual compact={compact} />
      case 'crypto':
        return <CryptoVisual compact={compact} host={product.appHost} />
      case 'masar':
        return <MasarVisual compact={compact} host={product.appHost} />
    }
  })()

  return (
    <div className={cn('relative isolate', className)}>
      {/* soft accent glow */}
      <div
        aria-hidden
        className={cn(
          'absolute -z-10 rounded-full blur-3xl',
          product.accent.glow,
          compact ? 'inset-6' : 'inset-x-10 inset-y-6'
        )}
      />
      {content}
    </div>
  )
}

/* ------------------------------------------------------------------ BMS */
function BmsVisual({ compact, host }: { compact: boolean; host: string }) {
  return (
    <div className={cn('relative', compact ? 'pe-8 sm:pe-10' : 'pe-10 pb-4 sm:pe-16 sm:pb-6')}>
      <BrowserFrame url={compact ? undefined : host}>
        <img
          src="/images/bms/dashboard_1.png"
          alt="BMS dashboard"
          loading={compact ? 'lazy' : 'eager'}
          className={cn('w-full object-cover object-right-top', compact ? 'aspect-[16/9]' : 'aspect-[16/10]')}
        />
      </BrowserFrame>
      <PhoneFrame className={cn('absolute end-0 bottom-0', compact ? 'w-[26%] -bottom-2 sm:-bottom-3' : 'w-[25%] sm:w-[24%]')}>
        <img
          src="/images/bms/mobile_1.png"
          alt="BMS mobile"
          loading="lazy"
          className="aspect-[9/18] w-full object-cover object-top"
        />
      </PhoneFrame>
    </div>
  )
}

/* ---------------------------------------------------------- School Bell */
function BellVisual({ compact }: { compact: boolean }) {
  return (
    <div className={cn('relative mx-auto flex items-end justify-center', compact ? 'gap-3' : 'gap-3 sm:gap-5')}>
      <PhoneFrame className={compact ? 'w-[38%]' : 'w-[42%] max-w-[260px]'}>
        <img
          src="/images/tdrem-bell/mobile-control.webp"
          alt="Tdrem School Bell remote control"
          loading={compact ? 'lazy' : 'eager'}
          className="aspect-[9/18] w-full object-cover object-top"
        />
      </PhoneFrame>
      <div className={cn('flex min-w-0 flex-col gap-2.5 sm:gap-3', compact ? 'w-[54%]' : 'w-[54%] max-w-[300px] sm:w-[50%]')}>
        <MiniCard>
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
            <Monitor className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">School PC</span>
            <span className="ms-auto inline-flex shrink-0 items-center gap-1 text-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Online
            </span>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">Next bell</div>
          <div className="text-xl font-bold leading-tight text-slate-900 sm:text-2xl">10:45</div>
          <div className="truncate text-[11px] text-slate-500">Break · Normal</div>
        </MiniCard>
        {!compact && (
          <MiniCard>
            <div className="space-y-1.5">
              {[
                ['08:00', 'Period 1'],
                ['08:45', 'Period 2'],
                ['09:30', 'Break'],
              ].map(([time, label], i) => (
                <div key={time} className="flex items-center gap-1.5 text-[11px] sm:gap-2 sm:text-xs">
                  <BellRing className={cn('h-3.5 w-3.5 shrink-0', i === 2 ? 'text-violet-600' : 'text-slate-400')} />
                  <span className="font-mono text-slate-700">{time}</span>
                  <span className="truncate text-slate-500">{label}</span>
                  <Check className="ms-auto h-3.5 w-3.5 shrink-0 text-emerald-500" />
                </div>
              ))}
            </div>
          </MiniCard>
        )}
        <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
          <span className="whitespace-nowrap rounded-lg bg-red-600 px-1.5 py-2 text-center text-[10px] font-semibold text-white shadow-sm sm:px-2 sm:text-[11px]">
            Fire alarm
          </span>
          <span className="whitespace-nowrap rounded-lg bg-amber-500 px-1.5 py-2 text-center text-[10px] font-semibold text-white shadow-sm sm:px-2 sm:text-[11px]">
            Lockdown
          </span>
        </div>
      </div>
    </div>
  )
}

/* --------------------------------------------------------------- Mosque */
function MosqueVisual({ compact }: { compact: boolean }) {
  return (
    <div className={cn('relative mx-auto flex items-end justify-center', compact ? 'gap-3' : 'gap-3 sm:gap-5')}>
      <PhoneFrame className={compact ? 'w-[38%]' : 'w-[42%] max-w-[260px]'}>
        <img
          src="/images/mosque/control-panel.webp"
          alt="Tdrem Mosque control panel"
          loading={compact ? 'lazy' : 'eager'}
          className="aspect-[9/18] w-full object-cover object-top"
        />
      </PhoneFrame>
      <div className={cn('flex min-w-0 flex-col gap-2.5 sm:gap-3', compact ? 'w-[54%]' : 'w-[54%] max-w-[300px] sm:w-[50%]')}>
        <MiniCard>
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
            <Monitor className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Mosque PC</span>
            <span className="ms-auto inline-flex shrink-0 items-center gap-1 text-emerald-600">
              <Wifi className="h-3 w-3" /> Live
            </span>
          </div>
          <div className="mt-2.5 flex items-center gap-2.5 sm:mt-3 sm:gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white sm:h-9 sm:w-9">
              <Volume2 className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="truncate text-[11px] text-slate-500">Now playing</div>
              <div className="truncate text-sm font-semibold text-slate-900" dir="rtl">
                الأذان
              </div>
            </div>
          </div>
          <div className="mt-3 flex h-6 items-end gap-[3px]" aria-hidden>
            {[40, 70, 55, 90, 65, 80, 45, 75, 60, 95, 50, 70, 40, 85, 55].map((h, i) => (
              <span key={i} className="w-full rounded-sm bg-emerald-500/70" style={{ height: `${h}%` }} />
            ))}
          </div>
        </MiniCard>
        {!compact && (
          <MiniCard>
            <div className="flex items-center gap-2 text-[11px] text-slate-600 sm:text-xs">
              <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
              <span className="truncate">Voice announcement</span>
              <span className="ms-auto shrink-0 font-mono text-slate-500">0:42</span>
            </div>
          </MiniCard>
        )}
      </div>
    </div>
  )
}

/* --------------------------------------------------------------- Crypto */
const TOKENS = [
  { sym: 'NOVA', chain: 'SOL', safety: 92, verdict: 'BUY', tone: 'bg-emerald-500/15 text-emerald-700', bar: 'bg-emerald-500' },
  { sym: 'ORBT', chain: 'BASE', safety: 81, verdict: 'ACCUMULATE', tone: 'bg-teal-500/15 text-teal-700', bar: 'bg-teal-500' },
  { sym: 'LUMA', chain: 'ETH', safety: 63, verdict: 'WATCH', tone: 'bg-amber-500/15 text-amber-700', bar: 'bg-amber-500' },
  { sym: 'HNYP', chain: 'BSC', safety: 8, verdict: 'AVOID', tone: 'bg-red-500/15 text-red-700', bar: 'bg-red-500' },
]

function CryptoVisual({ compact, host }: { compact: boolean; host: string }) {
  const rows = compact ? TOKENS.slice(0, 2).concat(TOKENS[3]) : TOKENS
  return (
    <BrowserFrame url={compact ? undefined : host} className="bg-white">
      <div className={cn('bg-white text-slate-900', compact ? 'p-3' : 'p-3.5 sm:p-5')}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Tokens</span>
          <span className="ms-auto inline-flex items-center gap-1.5 rounded-md border bg-slate-50 px-2 py-1 text-[11px] text-slate-400">
            <Search className="h-3 w-3 shrink-0" /> Check a token…
          </span>
        </div>
        {!compact && (
          <div className="mt-3 grid grid-cols-3 gap-1.5 sm:gap-2">
            {[
              ['Scanned today', '1,284'],
              ['Rugs blocked', '312'],
              ['Hit rate 30d', '64%'],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-slate-50 px-2 py-2 sm:px-2.5">
                <div className="truncate text-[10px] text-slate-500">{k}</div>
                <div className="text-xs font-bold sm:text-sm">{v}</div>
              </div>
            ))}
          </div>
        )}
        <ul className={cn('divide-y', compact ? 'mt-2' : 'mt-3')}>
          {rows.map((tk) => (
            <li key={tk.sym} className={cn('flex items-center gap-2.5 sm:gap-3', compact ? 'py-1.5' : 'py-2 sm:py-2.5')}>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-600">
                {tk.sym.slice(0, 2)}
              </span>
              <div className="min-w-0">
                <div className="text-xs font-semibold">{tk.sym}</div>
                <div className="text-[10px] text-slate-400">{tk.chain}</div>
              </div>
              <div className="ms-auto hidden w-20 sm:block">
                <div className="h-1.5 rounded-full bg-slate-100">
                  <div className={cn('h-1.5 rounded-full', tk.bar)} style={{ width: `${tk.safety}%` }} />
                </div>
                <div className="mt-0.5 text-[10px] text-slate-400">Safety {tk.safety}</div>
              </div>
              <span className={cn('ms-auto shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold sm:ms-0', tk.tone)}>
                {tk.verdict}
              </span>
            </li>
          ))}
        </ul>
        {!compact && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 px-2.5 py-2 text-[11px] leading-snug text-red-700 sm:items-center sm:px-3">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 sm:mt-0" />
            <span>HNYP: honeypot detected, sell tax 99%. Vetoed.</span>
          </div>
        )}
      </div>
    </BrowserFrame>
  )
}

/* ------------------------------------------------------------ Masar AI */
function MasarVisual({ compact, host }: { compact: boolean; host: string }) {
  return (
    <BrowserFrame url={compact ? undefined : host} className="bg-white">
      <div dir="rtl" className={cn('bg-white text-slate-900', compact ? 'p-3' : 'p-3.5 sm:p-5')}>
        <div className="flex items-center gap-3">
          <div className="min-w-0">
            <div className="text-[10px] text-slate-500">الوظيفة المستهدفة</div>
            <div className="truncate text-sm font-semibold">محلل بيانات · شركة طاقة، الرياض</div>
          </div>
          <div className="relative ms-auto h-11 w-11 shrink-0">
            <svg viewBox="0 0 36 36" className="h-11 w-11 -rotate-90">
              <circle cx="18" cy="18" r="15" fill="none" stroke="#f1f5f9" strokeWidth="4" />
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="#e11d48"
                strokeWidth="4"
                strokeDasharray="81 100"
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold">86%</span>
          </div>
        </div>

        <div className={cn('rounded-lg border bg-slate-50', compact ? 'mt-2 p-2' : 'mt-4 p-3')}>
          <div className="text-[10px] font-medium text-slate-500">سيرة ذاتية مخصصة · English / العربية</div>
          <p className="mt-1 text-xs leading-relaxed">
            حلّلتُ بيانات مبيعات 12 فرعاً وخفّضتُ وقت إعداد التقارير بنسبة 40٪ باستخدام SQL و Power BI.
          </p>
          <span className="mt-2 inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
            <Check className="h-3 w-3" /> من خبرتك المؤكدة
          </span>
        </div>

        {!compact && (
          <>
            <div className="mt-3 rounded-lg bg-rose-50 p-3">
              <div className="text-[10px] font-semibold text-rose-700">سؤال تدريبي للمقابلة</div>
              <p className="mt-1 text-xs leading-relaxed text-slate-700">
                حدّثني عن مشروع تعاملت فيه مع بيانات ناقصة. كيف اتخذت القرار؟
              </p>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {['مصدر: الموقع الرسمي', 'مصدر: التقرير السنوي'].map((s) => (
                <span key={s} className="rounded-md border px-1.5 py-0.5 text-[10px] text-slate-500">
                  {s}
                </span>
              ))}
              <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
                <Clock className="h-3 w-3" /> فجوة: Python
              </span>
            </div>
          </>
        )}
      </div>
    </BrowserFrame>
  )
}

function MiniCard({ children }: { children: ReactNode }) {
  return (
    <div dir="ltr" className="rounded-xl border border-slate-200 bg-white p-3 text-slate-900 shadow-soft">
      {children}
    </div>
  )
}
